import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Navigation } from 'lucide-react';

const StoreLocations = () => {
  const deliveryAreas = [
    { region: 'Greater Toronto Area', cities: ['Toronto', 'Mississauga', 'Brampton', 'Markham', 'Vaughan'] },
    { region: 'Ottawa Region', cities: ['Ottawa', 'Gatineau', 'Kanata', 'Orleans', 'Nepean'] },
    { region: 'Hamilton Area', cities: ['Hamilton', 'Burlington', 'Oakville', 'Milton', 'Ancaster'] },
    { region: 'Kitchener-Waterloo', cities: ['Kitchener', 'Waterloo', 'Cambridge', 'Guelph'] },
    { region: 'London Area', cities: ['London', 'St. Thomas', 'Strathroy', 'Woodstock'] },
  ];

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">Store Locations</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <MapPin className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Store Locations & Delivery Areas
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              We deliver premium furniture across Ontario with white glove service
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Online Store Notice */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12 animate-slide-up">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Navigation className="w-10 h-10 text-accent-600" />
            </div>
            <h2 className="text-3xl font-display font-bold text-dark-900 mb-4">
              Online Shopping Experience
            </h2>
            <p className="text-lg text-dark-600 font-body mb-6">
              Sky Furniture operates as an online-first retailer, allowing us to offer premium quality furniture at competitive prices. Browse our complete collection from the comfort of your home and enjoy convenient delivery right to your door.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-accent-50 border border-accent-200 rounded-xl text-accent-800 font-body">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">Shop 24/7 Online</span>
            </div>
          </div>
        </div>

        {/* Delivery Areas */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-4">
              We Deliver Across Ontario
            </h2>
            <p className="text-lg text-dark-600 font-body max-w-2xl mx-auto">
              Serving 116+ cities across Ontario with professional white glove delivery service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliveryAreas.map((area, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-card hover:shadow-soft transition-smooth p-6 border-l-4 border-primary-500"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-xl font-display font-bold text-dark-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary-600" />
                  {area.region}
                </h3>
                <ul className="space-y-2">
                  {area.cities.map((city, cityIndex) => (
                    <li key={cityIndex} className="text-dark-600 font-body flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-accent-500 rounded-full"></span>
                      {city}
                    </li>
                  ))}
                  <li className="text-dark-400 font-body italic text-sm">
                    + surrounding areas
                  </li>
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-accent text-white rounded-2xl shadow-colored p-8 md:p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-8 text-center">
              Can't Find Your City?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">Call Us</h3>
                <a href="tel:+16478726713" className="font-body hover:underline">
                  +1 (647) 872-6713
                </a>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">Email Us</h3>
                <a href="mailto:skyfurniturecanada@gmail.com" className="font-body hover:underline">
                  skyfurniturecanada@gmail.com
                </a>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">Response Time</h3>
                <p className="font-body">Within 24 hours</p>
              </div>
            </div>

            <p className="text-center text-accent-100 font-body mt-8">
              We're continuously expanding our delivery network. Contact us to check if we can deliver to your area!
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-card p-6 text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
              Wide Coverage
            </h3>
            <p className="text-dark-600 font-body">
              Delivering to 116+ cities across Ontario with reliable service
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 text-center">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Navigation className="w-8 h-8 text-accent-600" />
            </div>
            <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
              White Glove Service
            </h3>
            <p className="text-dark-600 font-body">
              Professional delivery team handles your furniture with care
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-success" />
            </div>
            <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
              Fast Delivery
            </h3>
            <p className="text-dark-600 font-body">
              Most orders delivered within 3-5 business days
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-primary text-white rounded-xl font-body font-semibold text-lg hover:shadow-colored transition-smooth"
          >
            <Mail className="w-6 h-6" />
            Contact Us About Delivery
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StoreLocations;